import { useRef, useState } from 'react'
import { lumi } from '@/lib/lumi'
import {ArrowLeft, TreePine, Upload, X, Loader2, Check, ChevronRight, ChevronLeft, AlertCircle, MapPin, AlertTriangle, CheckCircle2, Phone, Leaf, UserPlus, ShieldCheck} from 'lucide-react'
import { gerarProtocoloUnico, formatarCPF, formatarTelefone } from '@/utils/protocolo'
import { normalizarCPF, senhaDosCPF } from '@/utils/cidadaoAuth'
import type { EstadoArvore } from '@/types'
import toast from 'react-hot-toast'

const GREEN_DARK  = '#2d6124'
const GREEN_MID   = '#3a7d2c'
const TEAL        = '#0f3b50'

const ESTADOS_ARVORE: EstadoArvore[] = [
  'Saudável', 'Doente', 'Morta', 'Risco de Queda', 'Danificando Estrutura',
]

const STEPS = ['Seus Dados', 'Sobre a Árvore', 'Declaração', 'Confirmar']

function formatarCEP(v: string) {
  return v.replace(/\D/g, '').slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2')
}

interface Props {
  onClose: () => void
}

export default function SolicitacaoPublicaPage({ onClose }: Props) {
  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [protocolo, setProtocolo] = useState('')
  const [criandoCidadao, setCriandoCidadao] = useState(false)
  const [cidadaoCriado, setCidadaoCriado] = useState(false)
  const [erroCidadao, setErroCidadao] = useState('')
  const [fotos, setFotos] = useState<string[]>([])
  const [uploadingFoto, setUploadingFoto] = useState(false)
  const [buscandoCEP, setBuscandoCEP] = useState(false)
  const fotoInputRef = useRef<HTMLInputElement>(null)

  const [form, setForm] = useState({
    nomeCompleto: '',
    cpf: '',
    telefone: '',
    cep: '',
    cidadeSolicitante: '',
    bairroNome: '',
    ruaNome: '',
    numero: '',
    pontoReferencia: '',
    tipoArvore: '',
    quantidadeArvores: 1,
    porte: '' as 'Pequeno' | 'Médio' | 'Grande' | '',
    coordenadas: '',
    estadoArvore: '' as EstadoArvore | '',
    seraSubstituida: '' as 'sim' | 'nao' | '',
    qualArvoreReplantio: '',
    localReplantio: '' as 'mesmo' | 'outro' | '',
    ondeReplantio: '',
    motivoNaoSubstituicao: '',
    relatoProblema: '',
    termoAceito: false,
  })

  const update = (f: string, value: any) => setForm(prev => ({ ...prev, [f]: value }))

  const handleCEPBlur = async (cep: string) => {
    const raw = cep.replace(/\D/g, '')
    if (raw.length !== 8) return
    setBuscandoCEP(true)
    try {
      const res = await fetch(`https://viacep.com.br/ws/${raw}/json/`)
      const data = await res.json()
      if (!data.erro) {
        const cidade = data.localidade ? `${data.localidade}/${data.uf}` : ''
        setForm(prev => ({ ...prev, cidadeSolicitante: cidade }))
      }
    } catch { /* silencia */ }
    finally { setBuscandoCEP(false) }
  }

  const compressImage = (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const img = new Image()
      const objectUrl = URL.createObjectURL(file)
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const maxSize = 1024
        let w = img.width, h = img.height
        if (w > maxSize || h > maxSize) {
          if (w > h) { h = Math.round((h / w) * maxSize); w = maxSize }
          else { w = Math.round((w / h) * maxSize); h = maxSize }
        }
        canvas.width = w; canvas.height = h
        canvas.getContext('2d')!.drawImage(img, 0, 0, w, h)
        URL.revokeObjectURL(objectUrl)
        canvas.toBlob((blob) => {
          if (blob) resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }))
          else resolve(file)
        }, 'image/jpeg', 0.78)
      }
      img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(file) }
      img.src = objectUrl
    })
  }

  const handleFotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    if (fotos.length + files.length > 6) { toast.error('Máximo de 6 fotos permitidas.'); return }
    setUploadingFoto(true)
    try {
      const validFiles: File[] = []
      for (const file of files) {
        if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
          toast.error(`Formato inválido: ${file.name}`); continue
        }
        validFiles.push(await compressImage(file))
      }
      if (validFiles.length === 0) return
      const results = await lumi.tools.file.upload(validFiles)
      const urls: string[] = []
      for (const r of results) {
        if ((r as any).fileUrl) urls.push((r as any).fileUrl)
      }
      if (urls.length > 0) { setFotos(prev => [...prev, ...urls]); toast.success(`${urls.length} foto(s) adicionada(s)!`) }
    } catch {
      toast.error('Erro ao enviar foto.')
    } finally {
      setUploadingFoto(false)
      if (fotoInputRef.current) fotoInputRef.current.value = ''
    }
  }

  const validateStep = (): boolean => {
    if (step === 0) {
      if (!form.nomeCompleto.trim()) { toast.error('Informe o nome completo.'); return false }
      if (!form.cpf.trim()) { toast.error('Informe o CPF.'); return false }
      if (!form.telefone.trim()) { toast.error('Informe o telefone.'); return false }
      if (!form.bairroNome.trim()) { toast.error('Informe o bairro.'); return false }
      if (!form.ruaNome.trim()) { toast.error('Informe a rua.'); return false }
      if (!form.numero.trim()) { toast.error('Informe o número.'); return false }
    }
    if (step === 1) {
      if (!form.tipoArvore.trim()) { toast.error('Informe o tipo da árvore.'); return false }
      if (!form.porte) { toast.error('Selecione o porte da árvore.'); return false }
      if (!form.estadoArvore) { toast.error('Selecione o estado da árvore.'); return false }
      if (!form.seraSubstituida) { toast.error('Informe se a árvore será substituída.'); return false }
      if (form.seraSubstituida === 'sim') {
        if (!form.qualArvoreReplantio.trim()) { toast.error('Informe qual árvore será plantada.'); return false }
        if (!form.localReplantio) { toast.error('Informe o local de replantio.'); return false }
        if (form.localReplantio === 'outro' && !form.ondeReplantio.trim()) { toast.error('Informe onde será o replantio.'); return false }
      }
      if (form.seraSubstituida === 'nao' && !form.motivoNaoSubstituicao.trim()) {
        toast.error('Informe o motivo de não substituir a árvore.'); return false
      }
      if (!form.relatoProblema.trim()) { toast.error('Informe a causa do problema.'); return false }
    }
    if (step === 2) {
      if (!form.termoAceito) { toast.error('Você deve aceitar o termo de responsabilidade.'); return false }
    }
    return true
  }

  const handleNext = () => { if (!validateStep()) return; setStep(prev => Math.min(prev + 1, STEPS.length - 1)) }
  const handleBack = () => setStep(prev => Math.max(prev - 1, 0))

  /**
   * Cria conta cidadão em cidadaos_portal e em usuarios_sistema.
   * Pode ser chamado com cpf/nome diretos (após salvar) ou vazio (usa form).
   */
  const handleCriarAcessoCidadao = async (cpfParam?: string, nomeParam?: string) => {
    const cpfRaw = cpfParam ?? form.cpf
    const nome = nomeParam ?? form.nomeCompleto
    const cpfLimpo = normalizarCPF(cpfRaw)
    if (!cpfLimpo || cpfLimpo.length < 6 || !nome.trim()) {
      toast.error('Dados insuficientes para criar o acesso (CPF ou nome inválido).')
      return
    }
    setCriandoCidadao(true)
    setErroCidadao('')
    try {
      const now = new Date().toISOString()
      const senha = senhaDosCPF(cpfLimpo)

      // 1. Verifica/cria em cidadaos_portal
      let jaExistePortal = false
      try {
        const { list: lp } = await lumi.entities.cidadaos_portal.list({ filter: { cpf: cpfLimpo }, limit: 1 })
        jaExistePortal = !!(lp && lp.length > 0)
      } catch { /* segue para tentar criar */ }

      if (!jaExistePortal) {
        await lumi.entities.cidadaos_portal.create({
          cpf: cpfLimpo,
          nomeCompleto: nome,
          ativo: true,
          creator: 'sistema',
          createdAt: now,
          updatedAt: now,
        })
      }

      // 2. Verifica/cria em usuarios_sistema (perfil CIDADAO)
      let jaExisteUS = false
      try {
        const { list: lu } = await lumi.entities.usuarios_sistema.list({
          filter: { email: `${cpfLimpo}@cidadao.geoa` },
          limit: 1,
        })
        jaExisteUS = !!(lu && lu.length > 0)
      } catch { /* segue para tentar criar */ }

      if (!jaExisteUS) {
        await lumi.entities.usuarios_sistema.create({
          userId: `cidadao_${cpfLimpo}`,
          userName: nome,
          email: `${cpfLimpo}@cidadao.geoa`,
          cargo: 'Portal do Cidadão',
          userRole: 'CIDADAO',
          ativo: true,
          creator: 'sistema',
          createdAt: now,
          updatedAt: now,
        })
      }

      setCidadaoCriado(true)
      toast.success(`Acesso criado! Login: ${cpfLimpo} · Senha: ${senha}`, { duration: 8000 })
    } catch (err: unknown) {
      const detail = err instanceof Error ? err.message : String(err)
      console.error('[GEOA] Erro ao criar acesso cidadão:', detail, err)
      setErroCidadao(detail || 'Erro desconhecido. Tente novamente.')
      toast.error(`Erro ao criar acesso: ${detail || 'Tente novamente.'}`, { duration: 8000 })
    } finally {
      setCriandoCidadao(false)
    }
  }

  const handleSave = async () => {
    if (!validateStep()) return
    setSaving(true)
    try {
      const proto = await gerarProtocoloUnico()
      const now = new Date().toISOString()

      const payload = {
        protocolo: proto,
        origem: 'online' as const,
        nomeCompleto: form.nomeCompleto,
        cpf: form.cpf,
        telefone: form.telefone,
        cep: form.cep || undefined,
        cidadeSolicitante: form.cidadeSolicitante || undefined,
        bairroNome: form.bairroNome,
        ruaNome: form.ruaNome,
        numero: form.numero,
        pontoReferencia: form.pontoReferencia || undefined,
        tipoArvore: form.tipoArvore,
        quantidadeArvores: parseInt(String(form.quantidadeArvores), 10),
        porte: form.porte,
        coordenadas: form.coordenadas || undefined,
        estadoArvore: form.estadoArvore as EstadoArvore,
        seraSubstituida: form.seraSubstituida === 'sim',
        qualArvoreReplantio: form.seraSubstituida === 'sim' ? form.qualArvoreReplantio : undefined,
        localReplantio: form.seraSubstituida === 'sim' ? (form.localReplantio || undefined) : undefined,
        ondeReplantio: form.seraSubstituida === 'sim' && form.localReplantio === 'outro' ? form.ondeReplantio : undefined,
        motivoNaoSubstituicao: form.seraSubstituida === 'nao' ? form.motivoNaoSubstituicao : undefined,
        relatoProblema: form.relatoProblema,
        fotos: fotos.length > 0 ? fotos : undefined,
        termoAceito: form.termoAceito,
        assinaturasSolicitante: { nome: form.nomeCompleto, cpf: form.cpf },
        assinaturasSecretario: { cargo: 'Secretário Municipal de Meio Ambiente' },
        status: 'Em Análise' as const,
        creator: 'publico',
        createdAt: now,
        updatedAt: now,
      }

      const result = await lumi.entities.solicitacoes.create(payload) as any
      // Registra os dois status iniciais no histórico
      await lumi.entities.historico_status.create({
        solicitacaoId: result._id, protocolo: proto,
        status: 'Solicitação Recebida', responsavel: 'Portal Online',
        observacoes: 'Solicitação enviada pelo portal público de solicitações online.',
        creator: 'publico', createdAt: now, updatedAt: now,
      })
      await lumi.entities.historico_status.create({
        solicitacaoId: result._id, protocolo: proto,
        status: 'Em Análise', responsavel: 'Sistema',
        observacoes: 'Processo encaminhado automaticamente para análise técnica.',
        creator: 'sistema', createdAt: now, updatedAt: now,
      })

      setProtocolo(proto)
      setSaved(true)
    } catch (err: unknown) {
      const detail = err instanceof Error ? err.message : String(err)
      console.error('[GEOA] Erro ao enviar solicitação:', detail, err)
      toast.error(`Erro ao enviar solicitação: ${detail || 'Tente novamente.'}`, { duration: 7000 })
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3a7d2c] focus:border-transparent transition bg-white'
  const selectCls = `${inputCls}`
  const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5'

  // ── Tela de sucesso (após salvar) ──
  if (saved) {
    const cpfLimpo = normalizarCPF(form.cpf)
    const senhaExibir = senhaDosCPF(cpfLimpo)
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] py-10 px-4 text-center">
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mb-6 shadow-lg"
          style={{ backgroundColor: '#d1fae5' }}
        >
          <CheckCircle2 className="w-10 h-10" style={{ color: GREEN_MID }} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Solicitação Enviada!</h2>
        <p className="text-gray-500 mb-5 max-w-md leading-relaxed text-sm">
          Sua solicitação foi recebida com sucesso pela Prefeitura Municipal de São José do Seridó/RN.
        </p>

        {/* Protocolo */}
        <div
          className="rounded-2xl px-6 py-4 mb-5 w-full max-w-sm"
          style={{ backgroundColor: '#e8f1f5', border: '1px solid #b8d4e0' }}
        >
          <p className="text-xs text-gray-500 mb-1 font-medium uppercase tracking-wide">Seu protocolo</p>
          <p className="text-xl font-mono font-extrabold" style={{ color: TEAL }}>{protocolo}</p>
          <p className="text-xs text-gray-400 mt-1">Guarde este número para acompanhar sua solicitação</p>
        </div>

        {/* ── Bloco criar acesso cidadão ── */}
        <div className="w-full max-w-sm mb-5">
          {cidadaoCriado ? (
            <div
              className="rounded-2xl p-4 text-left"
              style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}
            >
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#16a34a' }} />
                <div>
                  <p className="text-sm font-bold text-green-800">Acesso ao Portal do Cidadão criado!</p>
                  <p className="text-xs text-green-700 mt-1 leading-relaxed">
                    Use os dados abaixo para acompanhar seus processos:
                  </p>
                  <div className="mt-2 space-y-1">
                    <p className="text-xs font-mono bg-green-100 rounded-lg px-3 py-1.5 text-green-900">
                      <span className="font-semibold">Login (CPF):</span> {cpfLimpo}
                    </p>
                    <p className="text-xs font-mono bg-green-100 rounded-lg px-3 py-1.5 text-green-900">
                      <span className="font-semibold">Senha:</span> {senhaExibir}
                    </p>
                  </div>
                  <p className="text-xs text-green-600 mt-2">Acesse o Portal do Cidadão na página inicial do sistema.</p>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="rounded-2xl p-4 text-left space-y-3"
              style={{ backgroundColor: '#eff6ff', border: '2px solid #bfdbfe' }}
            >
              <div className="flex items-start gap-3">
                <UserPlus className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#2563eb' }} />
                <div className="flex-1">
                  <p className="text-sm font-bold text-blue-900">Criar acesso ao Portal do Cidadão</p>
                  <p className="text-xs text-blue-700 mt-0.5 leading-relaxed">
                    Crie agora seu acesso para acompanhar esta e futuras solicitações.
                  </p>
                  <div className="mt-2 space-y-0.5 text-xs text-blue-800">
                    <p><span className="font-semibold">Login:</span> <span className="font-mono">{cpfLimpo}</span></p>
                    <p><span className="font-semibold">Senha:</span> <span className="font-mono">{senhaExibir}</span> <span className="text-blue-500">(6 primeiros dígitos do CPF)</span></p>
                  </div>
                </div>
              </div>
              {erroCidadao && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700 leading-relaxed">{erroCidadao}</p>
                </div>
              )}
              <button
                type="button"
                onClick={() => handleCriarAcessoCidadao(cpfLimpo, form.nomeCompleto)}
                disabled={criandoCidadao}
                className="w-full flex items-center justify-center gap-2 py-3 text-white text-sm font-bold rounded-2xl transition disabled:opacity-60"
                style={{ backgroundColor: '#2563eb' }}
                onMouseEnter={e => { if (!criandoCidadao) (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#1d4ed8' }}
                onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#2563eb'}
              >
                {criandoCidadao
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Criando acesso...</>
                  : <><UserPlus className="w-4 h-4" /> Criar Meu Acesso ao Portal</>}
              </button>
            </div>
          )}
        </div>

        {/* Aviso prazo de resposta */}
        <div
          className="flex items-start gap-3 rounded-2xl px-5 py-4 mb-3 w-full max-w-sm text-left"
          style={{ backgroundColor: '#fefce8', border: '1px solid #fde68a' }}
        >
          <Phone className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#d97706' }} />
          <div>
            <p className="text-sm font-semibold text-yellow-800">Retorno em até 15 dias úteis</p>
            <p className="text-xs text-yellow-700 mt-0.5 leading-relaxed">
              A Prefeitura entrará em contato com você pelo número <strong>{form.telefone}</strong> em até <strong>15 dias úteis</strong>.
            </p>
          </div>
        </div>

        {/* Aviso obrigação de replantio */}
        <div
          className="flex items-start gap-3 rounded-2xl px-5 py-4 mb-7 w-full max-w-sm text-left"
          style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}
        >
          <Leaf className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#dc2626' }} />
          <div>
            <p className="text-sm font-semibold text-red-800">Obrigação de Replantio</p>
            <p className="text-xs text-red-700 mt-0.5 leading-relaxed">
              Após a autorização, o solicitante tem até <strong>15 dias</strong> para plantar uma nova árvore,
              sob pena de multa conforme a <strong>Lei Municipal nº 426/2018</strong>.
            </p>
          </div>
        </div>

        <button
          onClick={onClose}
          className="px-8 py-3 text-white font-semibold rounded-2xl transition"
          style={{ backgroundColor: GREEN_MID }}
          onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.backgroundColor = GREEN_DARK}
          onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.backgroundColor = GREEN_MID}
        >
          Fechar
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-5 max-w-2xl mx-auto py-4">

      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Solicitação de Remoção de Árvore</h1>
          <p className="text-xs text-gray-400 mt-0.5">Prefeitura Municipal de São José do Seridó/RN</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="bg-white rounded-3xl border border-gray-100 px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between overflow-x-auto gap-1">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1 flex-shrink-0">
              <div className={`flex items-center gap-1.5 ${i <= step ? '' : 'opacity-40'}`}>
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition"
                  style={
                    i < step
                      ? { backgroundColor: GREEN_MID, color: '#fff' }
                      : i === step
                      ? { backgroundColor: GREEN_MID, color: '#fff', boxShadow: `0 0 0 4px ${GREEN_MID}22` }
                      : { backgroundColor: '#f3f4f6', color: '#6b7280' }
                  }
                >
                  {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </div>
                <span className={`text-xs font-medium hidden sm:inline ${i === step ? 'text-gray-900' : 'text-gray-400'}`}>
                  {s}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className="w-4 sm:w-8 h-0.5 rounded"
                  style={{ backgroundColor: i < step ? GREEN_MID : '#e5e7eb' }}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">

        {/* ── Step 0: Dados do cidadão ── */}
        {step === 0 && (
          <div className="space-y-5">
            <div className="pb-3 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900">Seus Dados Pessoais</h2>
              <p className="text-xs text-gray-400 mt-0.5">Preencha seus dados para que a prefeitura possa entrar em contato</p>
            </div>
            {/* Identificação */}
            <div className="pb-2 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Identificação</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className={labelCls}>Nome Completo <span className="text-red-500">*</span></label>
                <input value={form.nomeCompleto} onChange={e => update('nomeCompleto', e.target.value)}
                  placeholder="Seu nome completo" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>CPF <span className="text-red-500">*</span></label>
                <input value={form.cpf} onChange={e => update('cpf', formatarCPF(e.target.value))}
                  placeholder="000.000.000-00" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Telefone para Contato <span className="text-red-500">*</span></label>
                <input value={form.telefone} onChange={e => update('telefone', formatarTelefone(e.target.value))}
                  placeholder="(84) 99999-9999" className={inputCls} />
              </div>
            </div>

            {/* Endereço */}
            <div className="pb-2 border-b border-gray-100 mt-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Endereço</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Linha 1: Endereço (Rua) + Número */}
              <div>
                <label className={labelCls}>Endereço (Rua) <span className="text-red-500">*</span></label>
                <input value={form.ruaNome} onChange={e => update('ruaNome', e.target.value)}
                  placeholder="Nome da rua / logradouro" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Número <span className="text-red-500">*</span></label>
                <input value={form.numero} onChange={e => update('numero', e.target.value)}
                  placeholder="Nº" className={inputCls} />
              </div>
              {/* Linha 2: Bairro (largura total) */}
              <div className="sm:col-span-2">
                <label className={labelCls}>Bairro <span className="text-red-500">*</span></label>
                <input value={form.bairroNome} onChange={e => update('bairroNome', e.target.value)}
                  placeholder="Nome do bairro" className={inputCls} />
              </div>
              {/* Linha 3: CEP + Cidade */}
              <div>
                <label className={labelCls}>CEP</label>
                <div className="relative">
                  <input
                    value={form.cep}
                    onChange={e => update('cep', formatarCEP(e.target.value))}
                    onBlur={e => handleCEPBlur(e.target.value)}
                    placeholder="00000-000"
                    className={inputCls}
                    maxLength={9}
                  />
                  {buscandoCEP && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" style={{ color: GREEN_MID }} />
                  )}
                </div>
              </div>
              <div>
                <label className={labelCls}>Cidade</label>
                <div className="relative">
                  <input
                    value={form.cidadeSolicitante}
                    onChange={e => update('cidadeSolicitante', e.target.value)}
                    placeholder="Preenchido automaticamente pelo CEP"
                    className={`${inputCls} pr-9`}
                  />
                  {form.cidadeSolicitante && (
                    <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: GREEN_MID }} />
                  )}
                </div>
              </div>
              {/* Linha 4: Ponto de Referência */}
              <div className="sm:col-span-2">
                <label className={labelCls}>Ponto de Referência</label>
                <input value={form.pontoReferencia} onChange={e => update('pontoReferencia', e.target.value)}
                  placeholder="Ex: Próximo à escola, em frente ao mercado..." className={inputCls} />
              </div>
            </div>
          </div>
        )}

        {/* ── Step 1: Árvore + Fotos ── */}
        {step === 1 && (
          <div className="space-y-5">
            <div className="pb-3 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900">Dados da Árvore & Fotos</h2>
              <p className="text-xs text-gray-400 mt-0.5">Informações sobre a(s) árvore(s) e fotos do local</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Tipo da Árvore <span className="text-red-500">*</span></label>
                <input value={form.tipoArvore} onChange={e => update('tipoArvore', e.target.value)}
                  placeholder="Ex: Mangueira, Cajueiro..." className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Quantidade <span className="text-red-500">*</span></label>
                <input type="number" min={1} value={form.quantidadeArvores}
                  onChange={e => update('quantidadeArvores', e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Estado da Árvore <span className="text-red-500">*</span></label>
                <select value={form.estadoArvore} onChange={e => update('estadoArvore', e.target.value)} className={selectCls}>
                  <option value="">Selecione</option>
                  {ESTADOS_ARVORE.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
            </div>

            {/* Porte */}
            <div className="space-y-2">
              <label className={labelCls}>Porte da Árvore <span className="text-red-500">*</span></label>
              <div className="flex gap-3">
                {(['Pequeno', 'Médio', 'Grande'] as const).map(opt => (
                  <button key={opt} type="button"
                    onClick={() => update('porte', opt)}
                    className="flex-1 py-2.5 rounded-2xl text-sm font-semibold border-2 transition"
                    style={form.porte === opt
                      ? { backgroundColor: GREEN_MID, color: '#fff', borderColor: GREEN_MID }
                      : { borderColor: '#e5e7eb', color: '#4b5563' }}>
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            {/* Coordenadas opcional */}
            <div>
              <label className={labelCls}>Coordenadas GPS <span className="text-gray-400 font-normal normal-case">(opcional)</span></label>
              <input value={form.coordenadas} onChange={e => update('coordenadas', e.target.value)}
                placeholder="Ex: -6.8921, -36.8745" className={inputCls} />
            </div>

            <div className="space-y-3">
              <label className={labelCls}>A árvore será substituída? <span className="text-red-500">*</span></label>
              <div className="flex gap-3">
                {(['sim', 'nao'] as const).map(opt => (
                  <button key={opt} type="button"
                    onClick={() => {
                      update('seraSubstituida', opt)
                      if (opt === 'sim') {
                        // Pré-preenche com os dados da árvore a ser removida
                        if (!form.qualArvoreReplantio && form.tipoArvore) {
                          update('qualArvoreReplantio', form.tipoArvore)
                        }
                        if (!form.localReplantio) {
                          update('localReplantio', 'mesmo')
                        }
                      }
                    }}
                    className="flex-1 py-2.5 rounded-2xl text-sm font-semibold border-2 transition"
                    style={form.seraSubstituida === opt
                      ? { backgroundColor: GREEN_MID, color: '#fff', borderColor: GREEN_MID }
                      : { borderColor: '#e5e7eb', color: '#4b5563' }}>
                    {opt === 'sim' ? 'Sim' : 'Não'}
                  </button>
                ))}
              </div>
            </div>

            {form.seraSubstituida === 'sim' && (
              <div className="space-y-4 p-4 rounded-2xl border" style={{ backgroundColor: '#f0fdf4', borderColor: '#bbf7d0' }}>
                <div className="flex items-start gap-2 rounded-xl px-3 py-2" style={{ backgroundColor: '#dcfce7', border: '1px solid #bbf7d0' }}>
                  <span className="text-[10px] font-semibold uppercase tracking-wide mt-0.5" style={{ color: GREEN_DARK }}>ℹ</span>
                  <p className="text-xs leading-relaxed" style={{ color: GREEN_DARK }}>
                    Os campos abaixo foram pré-preenchidos com os dados da árvore a ser removida. Ajuste conforme necessário.
                  </p>
                </div>
                <div>
                  <label className={labelCls}>Qual árvore será plantada? <span className="text-red-500">*</span></label>
                  <input value={form.qualArvoreReplantio} onChange={e => update('qualArvoreReplantio', e.target.value)}
                    placeholder="Ex: Ipê-amarelo, Angico..." className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Local do Replantio <span className="text-red-500">*</span></label>
                  <div className="flex gap-3">
                    {(['mesmo', 'outro'] as const).map(opt => (
                      <button key={opt} type="button" onClick={() => {
                        update('localReplantio', opt)
                        if (opt === 'outro' && !form.ondeReplantio) {
                          // Pré-preenche com o endereço da solicitação
                          const endSol = [form.ruaNome, form.numero ? `nº ${form.numero}` : '', form.bairroNome].filter(Boolean).join(', ')
                          if (endSol) update('ondeReplantio', endSol)
                        }
                      }}
                        className="flex-1 py-2.5 rounded-2xl text-sm font-semibold border-2 transition"
                        style={form.localReplantio === opt
                          ? { backgroundColor: GREEN_MID, color: '#fff', borderColor: GREEN_MID }
                          : { borderColor: '#bbf7d0', color: GREEN_MID }}>
                        {opt === 'mesmo' ? 'Mesmo Local' : 'Outro Local'}
                      </button>
                    ))}
                  </div>
                </div>
                {form.localReplantio === 'outro' && (
                  <div>
                    <label className={labelCls}>Onde será o replantio? <span className="text-red-500">*</span></label>
                    <textarea value={form.ondeReplantio} onChange={e => update('ondeReplantio', e.target.value)}
                      placeholder="Descreva o local..." rows={2} className={`${inputCls} resize-none`} />
                  </div>
                )}
              </div>
            )}

            {form.seraSubstituida === 'nao' && (
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200">
                  <label className={labelCls}>Motivo da não substituição <span className="text-red-500">*</span></label>
                  <textarea value={form.motivoNaoSubstituicao} onChange={e => update('motivoNaoSubstituicao', e.target.value)}
                    placeholder="Descreva o motivo..." rows={3} className={`${inputCls} resize-none border-amber-300 focus:ring-amber-500`} />
                </div>
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700 leading-relaxed">
                    O não plantio de uma árvore substituta pode gerar a cobrança de uma <strong>taxa de compensação ambiental</strong> conforme a legislação municipal.
                  </p>
                </div>
              </div>
            )}

            <div>
              <label className={labelCls}>Descrição do Problema <span className="text-red-500">*</span></label>
              <textarea value={form.relatoProblema} onChange={e => update('relatoProblema', e.target.value)}
                placeholder="Descreva detalhadamente o problema, risco de queda, danos causados..." rows={4}
                className={`${inputCls} resize-none`} />
            </div>

            <div className="pt-2 border-t border-gray-100 space-y-3">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Fotos do Local</p>
                <p className="text-xs text-gray-400 mt-0.5">Até 6 fotos — JPG, PNG ou WEBP (opcional)</p>
              </div>
              <input ref={fotoInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp"
                multiple onChange={handleFotoUpload} className="hidden" />
              {fotos.length < 6 && (
                <button type="button" onClick={() => fotoInputRef.current?.click()} disabled={uploadingFoto}
                  className="flex flex-col items-center justify-center gap-2 w-full py-6 border-2 border-dashed border-gray-200 rounded-2xl text-gray-500 hover:text-green-600 hover:border-green-300 hover:bg-green-50 transition disabled:opacity-50 disabled:cursor-not-allowed">
                  {uploadingFoto ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /><span className="text-sm font-medium">Enviando...</span></>
                  ) : (
                    <><Upload className="w-5 h-5" /><span className="text-sm font-medium">Clique para adicionar fotos</span>
                    <span className="text-xs text-gray-400">{fotos.length}/6 fotos</span></>
                  )}
                </button>
              )}
              {fotos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {fotos.map((url, i) => (
                    <div key={i} className="relative group rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
                      <img src={url} alt={`Foto ${i + 1}`} className="w-full h-28 object-cover" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                        <button type="button" onClick={() => setFotos(prev => prev.filter((_, idx) => idx !== i))}
                          className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white shadow-lg">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-30 py-1">
                        <span className="text-xs text-white font-medium px-2">Foto {i + 1}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Step 2: Termo ── */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="pb-3 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900">Declaração do Solicitante</h2>
              <p className="text-xs text-gray-400 mt-0.5">Leia atentamente e confirme o aceite</p>
            </div>
            <div className="bg-gray-50 rounded-2xl border border-gray-200 p-5 text-sm text-gray-700 leading-relaxed space-y-3">
              <p>Declaro que todas as informações prestadas nesta solicitação são verdadeiras e estão em conformidade com a <strong>Lei Municipal nº 426, de 21 de novembro de 2018</strong>.</p>
              <p>Estou ciente de que informações falsas ou procedimentos realizados sem autorização estarão sujeitos às penalidades previstas na legislação municipal.</p>
              <p className="font-medium text-gray-800">Art. 22. Em casos de realização de podas, cortes ou remoções não autorizadas, ficam os infratores passíveis das penalidades estabelecidas na presente Lei, como advertência em caso do munícipe ser primário e, se reincidente, multa variando de 10% a 100% do salário mínimo vigente, de acordo com a reincidência.</p>
            </div>
            <label className={`flex items-start gap-3 cursor-pointer p-4 rounded-2xl border-2 transition ${form.termoAceito ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'}`}>
              <input type="checkbox" checked={form.termoAceito}
                onChange={e => update('termoAceito', e.target.checked)}
                className="mt-0.5 w-4 h-4 flex-shrink-0"
                style={{ accentColor: GREEN_MID }} />
              <span className="text-sm text-gray-700 font-medium">
                Li e concordo com os termos acima e declaro que as informações são verdadeiras.
              </span>
            </label>
            {!form.termoAceito && (
              <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs font-medium">Você precisa aceitar o termo para enviar a solicitação.</span>
              </div>
            )}
          </div>
        )}

        {/* ── Step 3: Confirmar / Resumo ── */}
        {step === 3 && (
          <div className="space-y-5">
            <div className="pb-3 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900">Confirmar Envio</h2>
              <p className="text-xs text-gray-400 mt-0.5">Revise os dados antes de enviar</p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Resumo</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-400">Nome:</span> <span className="font-medium text-gray-800">{form.nomeCompleto || '—'}</span></div>
                <div><span className="text-gray-400">CPF:</span> <span className="font-medium text-gray-800">{form.cpf || '—'}</span></div>
                <div><span className="text-gray-400">Telefone:</span> <span className="font-medium text-gray-800">{form.telefone || '—'}</span></div>
                <div><span className="text-gray-400">Bairro:</span> <span className="font-medium text-gray-800">{form.bairroNome || '—'}</span></div>
                <div><span className="text-gray-400">Rua:</span> <span className="font-medium text-gray-800">{form.ruaNome || '—'}, {form.numero}</span></div>
                <div><span className="text-gray-400">Árvore:</span> <span className="font-medium text-gray-800">{form.tipoArvore || '—'}</span></div>
                <div><span className="text-gray-400">Substituição:</span> <span className={`font-medium ${form.seraSubstituida === 'sim' ? 'text-green-700' : 'text-red-600'}`}>{form.seraSubstituida === 'sim' ? 'Sim' : form.seraSubstituida === 'nao' ? 'Não' : '—'}</span></div>
                <div><span className="text-gray-400">Fotos:</span> <span className="font-medium text-gray-800">{fotos.length} foto(s)</span></div>
              </div>
            </div>

            {/* Aviso: o acesso será criado na tela de sucesso */}
            <div
              className="flex items-start gap-3 rounded-2xl p-4"
              style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}
            >
              <UserPlus className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#2563eb' }} />
              <div>
                <p className="text-sm font-semibold text-blue-900">Acesso ao Portal do Cidadão</p>
                <p className="text-xs text-blue-700 mt-0.5 leading-relaxed">
                  Após enviar a solicitação, você poderá criar seu acesso ao portal para acompanhar o processo.
                  O <strong>login</strong> será o CPF <strong>({normalizarCPF(form.cpf) || '—'})</strong> e
                  a <strong>senha</strong> os <strong>6 primeiros dígitos</strong>.
                </p>
              </div>
            </div>

            <div
              className="flex items-start gap-3 rounded-2xl p-4"
              style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}
            >
              <Phone className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#d97706' }} />
              <div>
                <p className="text-sm font-semibold text-yellow-800">Retorno em até 15 dias úteis</p>
                <p className="text-xs text-yellow-700 mt-0.5">
                  Após o envio, a Prefeitura entrará em contato pelo número <strong>{form.telefone}</strong> em até <strong>15 dias úteis</strong>.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navegação */}
      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={handleBack} disabled={step === 0}
          className="flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-2xl hover:bg-gray-50 disabled:opacity-30 transition">
          <ChevronLeft className="w-4 h-4" />
          Anterior
        </button>

        {step < STEPS.length - 1 ? (
          <button type="button" onClick={handleNext}
            className="flex items-center gap-2 px-6 py-2.5 text-white text-sm font-semibold rounded-2xl transition shadow-sm"
            style={{ backgroundColor: GREEN_MID }}
            onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.backgroundColor = GREEN_DARK}
            onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.backgroundColor = GREEN_MID}>
            Próximo
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button type="button" onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 text-white text-sm font-semibold rounded-2xl transition shadow-sm disabled:opacity-60"
            style={{ backgroundColor: GREEN_MID }}
            onMouseEnter={e => { if (!saving) (e.currentTarget as HTMLButtonElement).style.backgroundColor = GREEN_DARK }}
            onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.backgroundColor = GREEN_MID}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <TreePine className="w-4 h-4" />}
            {saving ? 'Enviando...' : 'Enviar Solicitação'}
          </button>
        )}
      </div>
    </div>
  )
}
